import React from 'react';
import { useParams, Navigate, Link, useLocation } from 'react-router-dom';
import { getServiceById } from '../data/services.js';
import ServiceFailureAnalysis from './ServiceFailureAnalysis';
import CostComparisonTable from './CostComparisonTable';
import CTA from './CTA';
import { generateCTAMessage } from '../utils/ctaMessages.js';
import { scrollToTopIfSamePage } from '../utils/scrollToTop';

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const service = getServiceById(serviceId);
  const location = useLocation();

  // Handle invalid service ID
  if (!service) {
    return <Navigate to="/services" replace />;
  }

  return (
    <>
      {/* Hero Section - Mobile Responsive */}
      <section className="service-hero-section" style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
        position: 'relative',
        overflow: 'hidden',
        minHeight: 'clamp(70vh, 80vh, 90vh)',
        display: 'flex',
        alignItems: 'center',
        paddingTop: 'clamp(100px, 15vw, 140px)',
        paddingBottom: 'clamp(2rem, 6vw, 4rem)'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.04) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>

        {/* Floating Particles */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          pointerEvents: 'none'
        }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{
              position: 'absolute',
              width: '6px',
              height: '6px',
              background: 'rgba(56, 189, 248, 0.4)',
              borderRadius: '50%',
              top: `${15 + i * 12}%`,
              left: `${8 + i * 15}%`,
              animation: `float ${4 + i * 0.3}s ease-in-out infinite`,
              animationDelay: `${i * 0.4}s`
            }}></div>
          ))}
        </div>

        <div className="container" style={{ position: 'relative', zIndex: 3 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              {/* Service Icon - Mobile Responsive */}
              <div className="service-icon-large mb-4" style={{
                display: 'inline-block',
                background: 'rgba(30, 41, 59, 0.8)',
                border: '2px solid rgba(14, 165, 233, 0.3)',
                borderRadius: '50%',
                padding: 'clamp(1rem, 3vw, 2rem)',
                backdropFilter: 'blur(15px)',
                boxShadow: '0 8px 25px rgba(14, 165, 233, 0.2)'
              }}>
                <i className={`bi ${service.icon}`} style={{ 
                  fontSize: 'clamp(2.5rem, 6vw, 4rem)', 
                  color: '#0ea5e9' 
                }}></i>
              </div>

              {/* Service Title - Mobile Responsive */}
              <h1 style={{
                fontSize: 'clamp(1.8rem, 5vw, 3rem)',
                fontWeight: '800',
                color: '#ffffff',
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                lineHeight: '1.2',
                marginBottom: 'clamp(1rem, 3vw, 2rem)',
                padding: '0 clamp(0.5rem, 2vw, 1rem)'
              }}>
                {service.title}
              </h1>

              {/* Service Subtitle - Mobile Responsive */}
              <h2 style={{
                fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: '400',
                lineHeight: '1.4',
                marginBottom: 'clamp(1rem, 3vw, 2rem)',
                padding: '0 clamp(0.5rem, 2vw, 1rem)'
              }}>
                {service.subtitle}
              </h2>

              {/* Service Description - Mobile Responsive */}
              <p style={{
                fontSize: 'clamp(0.9rem, 2.2vw, 1.1rem)',
                color: 'rgba(255, 255, 255, 0.8)',
                maxWidth: 'clamp(300px, 80vw, 800px)',
                margin: '0 auto clamp(1.5rem, 4vw, 3rem)',
                lineHeight: '1.6',
                padding: '0 clamp(0.5rem, 2vw, 1rem)'
              }}>
                {service.description}
              </p>

              {/* CTA Buttons - Mobile Responsive */}
              <div className="hero-cta" style={{
                display: 'flex',
                gap: 'clamp(1rem, 3vw, 2rem)',
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginBottom: 'clamp(1.5rem, 4vw, 3rem)',
                marginTop: 'clamp(1rem, 3vw, 2rem)',
                padding: '0 clamp(0.5rem, 2vw, 1rem)'
              }}>
                <Link 
                  to="/contact" 
                  onClick={() => scrollToTopIfSamePage(location.pathname, '/contact')}
                  style={{
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                    border: 'none',
                    color: '#ffffff',
                    padding: 'clamp(1rem, 3vw, 1.6rem) clamp(2rem, 6vw, 4rem)',
                    borderRadius: 'clamp(30px, 8vw, 60px)',
                    fontWeight: '800',
                    fontSize: 'clamp(0.9rem, 2.2vw, 1.2rem)',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'clamp(0.5rem, 1.5vw, 1rem)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 15px 40px rgba(14, 165, 233, 0.5), 0 8px 25px rgba(0, 0, 0, 0.15)',
                    minWidth: 'clamp(200px, 40vw, 280px)',
                    width: 'clamp(200px, 40vw, 280px)',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                    }}
                    onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-8px) scale(1.05)';
                    e.target.style.boxShadow = '0 25px 50px rgba(14, 165, 233, 0.6), 0 15px 35px rgba(0, 0, 0, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0) scale(1)';
                    e.target.style.boxShadow = '0 15px 40px rgba(14, 165, 233, 0.5), 0 8px 25px rgba(0, 0, 0, 0.15)';
                  }}>
                    <i className="bi bi-people-fill" style={{ fontSize: 'clamp(1rem, 2.5vw, 1.3rem)' }}></i>
                    {serviceId === 'staff-augmentation' ? 'Hire Talent Now' : 'Start Your Project'}
                  </Link>
                
                <Link 
                  to="/case-studies" 
                  onClick={() => scrollToTopIfSamePage(location.pathname, '/case-studies')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '2px solid rgba(14, 165, 233, 0.4)',
                    color: '#ffffff',
                    padding: 'clamp(1rem, 3vw, 1.6rem) clamp(2rem, 6vw, 4rem)',
                    borderRadius: 'clamp(30px, 8vw, 60px)',
                    fontWeight: '800',
                    fontSize: 'clamp(0.9rem, 2.2vw, 1.2rem)',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'clamp(0.5rem, 1.5vw, 1rem)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    backdropFilter: 'blur(25px)',
                    minWidth: 'clamp(200px, 40vw, 280px)',
                    width: 'clamp(200px, 40vw, 280px)',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                    }}
                    onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-8px) scale(1.05)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.borderColor = 'rgba(14, 165, 233, 0.8)';
                    e.target.style.boxShadow = '0 25px 50px rgba(14, 165, 233, 0.3), 0 15px 35px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0) scale(1)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.borderColor = 'rgba(14, 165, 233, 0.4)';
                      e.target.style.boxShadow = 'none';
                  }}>
                    <i className="bi bi-calendar-check" style={{ fontSize: 'clamp(1rem, 2.5vw, 1.3rem)' }}></i>
                    {serviceId === 'staff-augmentation' ? 'Schedule Interview' : 'See Results'}
                  </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Projects Fail | How We Add Value Section */}
      <ServiceFailureAnalysis serviceId={serviceId} />

      {/* Staff Augmentation Cost Comparison - Only for staff-augmentation service */}
      {serviceId === 'staff-augmentation' && (
        <CostComparisonTable />
      )}

      {/* Staff Augmentation Specialized Sections */}
      {serviceId === 'staff-augmentation' && (
        <>
          {/* Talent Profiles Section - Mobile Responsive */}
          <section className="talent-profiles-section py-5" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            position: 'relative',
            overflow: 'hidden',
            padding: 'clamp(3rem, 8vw, 6rem) 0'
          }}>
            <div className="container" style={{ position: 'relative', zIndex: 2 }}>
              <div className="row">
                <div className="col-lg-8 mx-auto text-center mb-5">
                  <h2 style={{
                    fontSize: 'clamp(2rem, 4vw, 3rem)',
                    fontWeight: '800',
                    color: '#1e293b',
                    marginBottom: '1rem'
                  }}>
                    Our Talent Pool
                  </h2>
                  <p style={{
                    fontSize: 'clamp(1rem, 2.2vw, 1.2rem)',
                    color: '#64748b',
                    lineHeight: '1.6'
                  }}>
                    Pre-vetted senior professionals ready to integrate into your team
                  </p>
                </div>
              </div>
              
              <div className="row g-4">
                {service.talentProfiles?.map((profile, index) => (
                  <div key={index} className="col-12 col-lg-6">
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(14, 165, 233, 0.1)',
                      borderRadius: 'clamp(12px, 3vw, 20px)',
                      padding: 'clamp(1.5rem, 4vw, 2.5rem)',
                      height: '100%',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 20px 40px rgba(14, 165, 233, 0.15)';
                      e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.06)';
                      e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.1)';
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'clamp(0.75rem, 2vw, 1rem)',
                        marginBottom: 'clamp(1rem, 2.5vw, 1.5rem)'
                      }}>
                        <div style={{
                          width: 'clamp(50px, 10vw, 60px)',
                          height: 'clamp(50px, 10vw, 60px)',
                          background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
                          flexShrink: 0
                        }}>
                          <i className="bi bi-person-check"></i>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h4 style={{
                            color: '#1e293b',
                            marginBottom: 'clamp(0.125rem, 0.5vw, 0.25rem)',
                            fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)',
                            fontWeight: '700',
                            lineHeight: '1.2'
                          }}>
                            {profile.role}
                          </h4>
                          <p style={{
                            color: '#64748b',
                            margin: 0,
                            fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
                            lineHeight: '1.3'
                          }}>
                            {profile.experience} experience
                          </p>
                        </div>
                      </div>
                      
                      <div style={{ marginBottom: 'clamp(1rem, 2.5vw, 1.5rem)' }}>
                        <h5 style={{
                          color: '#374151',
                          marginBottom: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                          fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                          fontWeight: '600'
                        }}>
                          Key Skills:
                        </h5>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 'clamp(0.375rem, 1vw, 0.5rem)'
                        }}>
                          {profile.skills.map((skill, skillIndex) => (
                            <span key={skillIndex} style={{
                              background: 'rgba(14, 165, 233, 0.1)',
                              color: '#0ea5e9',
                              padding: 'clamp(0.25rem, 0.5vw, 0.25rem) clamp(0.5rem, 1.5vw, 0.75rem)',
                              borderRadius: 'clamp(15px, 3vw, 20px)',
                              fontSize: 'clamp(0.75rem, 1.8vw, 0.85rem)',
                              fontWeight: '500',
                              lineHeight: '1.3'
                            }}>
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                        paddingTop: 'clamp(0.75rem, 2vw, 1rem)',
                        borderTop: '1px solid rgba(14, 165, 233, 0.1)'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '0.5rem'
                        }}>
                          <span style={{
                            color: '#64748b',
                            fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
                            fontWeight: '500'
                          }}>
                            Availability: 
                          </span>
                          <span style={{
                            color: '#10b981',
                            fontWeight: '600',
                            fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)'
                          }}>
                            {profile.availability}
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '0.5rem'
                        }}>
                          <span style={{
                            color: '#64748b',
                            fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
                            fontWeight: '500'
                          }}>
                            Rate: 
                          </span>
                          <span style={{
                            color: '#1e293b',
                            fontWeight: '600',
                            fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)'
                          }}>
                            {profile.rate}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Engagement Models Section - Mobile Responsive */}
          <section className="engagement-models-section py-5" style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            position: 'relative',
            overflow: 'hidden',
            padding: 'clamp(3rem, 8vw, 6rem) 0'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.08) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.06) 0%, transparent 50%)
              `,
              zIndex: 1
            }}></div>

            <div className="container" style={{ position: 'relative', zIndex: 2 }}>
              <div className="row">
                <div className="col-lg-8 mx-auto text-center mb-5">
                  <h2 style={{
                    fontSize: 'clamp(2rem, 4vw, 3rem)',
                    fontWeight: '800',
                    color: '#ffffff',
                    marginBottom: '1rem'
                  }}>
                    Flexible Engagement Models
                  </h2>
                  <p style={{
                    fontSize: 'clamp(1rem, 2.2vw, 1.2rem)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    lineHeight: '1.6'
                  }}>
                    Choose the engagement model that best fits your project needs
                  </p>
                </div>
              </div>
              
              <div className="row g-4">
                {service.engagementModels?.map((model, index) => (
                  <div key={index} className="col-12 col-lg-6">
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(14, 165, 233, 0.2)',
                      borderRadius: 'clamp(12px, 3vw, 20px)',
                      padding: 'clamp(1.5rem, 4vw, 2.5rem)',
                      height: '100%',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                      e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
                    }}>
                      <h4 style={{
                        color: '#ffffff',
                        marginBottom: 'clamp(0.75rem, 2vw, 1rem)',
                        fontSize: 'clamp(1.2rem, 3vw, 1.4rem)',
                        fontWeight: '700',
                        lineHeight: '1.3'
                      }}>
                        {model.model}
                      </h4>
                      
                      <p style={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        marginBottom: 'clamp(1rem, 2.5vw, 1.5rem)',
                        lineHeight: '1.6',
                        fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                      }}>
                        {model.description}
                      </p>
                      
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                        paddingTop: 'clamp(0.75rem, 2vw, 1rem)',
                        borderTop: '1px solid rgba(14, 165, 233, 0.2)'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '0.5rem'
                        }}>
                          <span style={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
                            fontWeight: '500'
                          }}>
                            Duration: 
                          </span>
                          <span style={{
                            color: '#ffffff',
                            fontWeight: '600',
                            fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)'
                          }}>
                            {model.duration}
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '0.5rem'
                        }}>
                          <span style={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
                            fontWeight: '500'
                          }}>
                            Commitment: 
                          </span>
                          <span style={{
                            color: '#ffffff',
                            fontWeight: '600',
                            fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)'
                          }}>
                            {model.commitment}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Service Details Section - Mobile Responsive */}
      <section className="service-details-section py-5" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: 'clamp(3rem, 8vw, 8rem) 0'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(14, 165, 233, 0.06) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>

        <div className="container" style={{ position: 'relative', zIndex: 2, maxWidth: '1400px' }}>
          <div className="row">
            <div className="col-12">
              {/* Service Icon */}
              <div className="text-center mb-5">
                <div style={{
                  width: 'clamp(70px, 12vw, 100px)',
                  height: 'clamp(70px, 12vw, 100px)',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                  borderRadius: 'clamp(16px, 3vw, 24px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
                  margin: '0 auto clamp(1rem, 3vw, 2rem)',
                  boxShadow: '0 10px 30px rgba(14, 165, 233, 0.3)',
                  transition: 'all 0.3s ease'
                }}>
                  <i className={`bi ${service.icon}`}></i>
                </div>
              </div>

              {/* Staff Augmentation Specific Content */}
              {serviceId === 'staff-augmentation' ? (
                <>
                  {/* Hiring Process Section */}
                  <div className="mb-6">
                    <div className="text-center mb-5">
                      <h3 style={{
                        fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                        fontWeight: '800',
                        color: '#1e293b',
                        marginBottom: '1rem',
                        letterSpacing: '-0.02em'
                      }}>
                        Our Hiring Process
                      </h3>
                      <p style={{
                        fontSize: 'clamp(1rem, 2.2vw, 1.1rem)',
                        color: '#64748b',
                        maxWidth: 'clamp(300px, 80vw, 600px)',
                        margin: '0 auto',
                        lineHeight: '1.6'
                      }}>
                        Streamlined process to find and onboard the perfect talent for your team
                      </p>
                    </div>
                    <div className="row g-4">
                      {service.process?.map((step, index) => (
                        <div key={index} className="col-12 col-lg-6">
                          <div style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(14, 165, 233, 0.1)',
                            borderRadius: 'clamp(12px, 3vw, 20px)',
                            padding: 'clamp(1.5rem, 4vw, 2rem)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'clamp(1rem, 2.5vw, 1.5rem)',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(14, 165, 233, 0.15)';
                            e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0) scale(1)';
                            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.04)';
                            e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.1)';
                          }}>
                            <div style={{
                              width: 'clamp(45px, 10vw, 50px)',
                              height: 'clamp(45px, 10vw, 50px)',
                              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                              borderRadius: 'clamp(12px, 2.5vw, 16px)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                              flexShrink: 0,
                              boxShadow: '0 8px 20px rgba(14, 165, 233, 0.3)',
                              transition: 'all 0.3s ease'
                            }}>
                              <span style={{ fontWeight: 'bold' }}>{index + 1}</span>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <h6 style={{ 
                                color: '#1e293b', 
                                fontWeight: '700',
                                fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                                margin: 0,
                                lineHeight: '1.4'
                              }}>
                                {step.step}
                              </h6>
                              <p style={{
                                color: '#64748b',
                                fontSize: 'clamp(0.85rem, 2vw, 0.9rem)',
                                margin: 0,
                                marginTop: 'clamp(0.125rem, 0.5vw, 0.25rem)',
                                lineHeight: '1.4'
                              }}>
                                {step.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Talent Benefits Section */}
                  <div className="mb-6" style={{ marginTop: '4rem' }}>
                    <div className="text-center mb-5">
                      <h3 style={{
                        fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                        fontWeight: '800',
                        color: '#1e293b',
                        marginBottom: '1rem',
                        letterSpacing: '-0.02em'
                      }}>
                        Why Choose Our Talent
                      </h3>
                      <p style={{
                        fontSize: 'clamp(1rem, 2.2vw, 1.1rem)',
                        color: '#64748b',
                        maxWidth: 'clamp(300px, 80vw, 600px)',
                        margin: '0 auto',
                        lineHeight: '1.6'
                      }}>
                        Pre-vetted professionals who deliver exceptional results
                      </p>
                    </div>
                    <div className="row g-4">
                      {service.talentBenefits?.map((benefit, index) => (
                        <div key={index} className="col-12 col-lg-6">
                          <div style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(14, 165, 233, 0.1)',
                            borderRadius: 'clamp(12px, 3vw, 20px)',
                            padding: 'clamp(1.5rem, 4vw, 2rem)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'clamp(1rem, 2.5vw, 1.5rem)',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(14, 165, 233, 0.15)';
                            e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0) scale(1)';
                            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.04)';
                            e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.1)';
                          }}>
                            <div style={{
                              width: 'clamp(45px, 10vw, 50px)',
                              height: 'clamp(45px, 10vw, 50px)',
                              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                              borderRadius: 'clamp(12px, 2.5vw, 16px)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                              flexShrink: 0,
                              boxShadow: '0 8px 20px rgba(14, 165, 233, 0.3)',
                              transition: 'all 0.3s ease'
                            }}>
                              <i className="bi bi-check-lg"></i>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <h6 style={{ 
                                color: '#1e293b', 
                                fontWeight: '700',
                                fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                                margin: 0,
                                lineHeight: '1.4'
                              }}>
                                {benefit}
                              </h6>
                              <p style={{
                                color: '#64748b',
                                fontSize: 'clamp(0.85rem, 2vw, 0.9rem)',
                                margin: 0,
                                marginTop: 'clamp(0.125rem, 0.5vw, 0.25rem)',
                                lineHeight: '1.4'
                              }}>
                                Guaranteed quality
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                /* Generic Service Features for other services */
                service.features && (
                  <div className="mb-6">
                    <div className="text-center mb-5">
                      <h3 style={{
                        fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                        fontWeight: '800',
                        color: '#1e293b',
                        marginBottom: '1rem',
                        letterSpacing: '-0.02em'
                      }}>
                        What You Get
                      </h3>
                      <p style={{
                        fontSize: '1.1rem',
                        color: '#64748b',
                        maxWidth: '600px',
                        margin: '0 auto',
                        lineHeight: '1.6'
                      }}>
                        Proven solutions that deliver measurable results
                      </p>
                    </div>
                    <div className="row g-4">
                      {service.features.map((feature, index) => (
                        <div key={index} className="col-lg-6">
                          <div style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(14, 165, 233, 0.1)',
                            borderRadius: '20px',
                            padding: '2rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1.5rem',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(14, 165, 233, 0.15)';
                            e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0) scale(1)';
                            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.04)';
                            e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.1)';
                          }}>
                            <div style={{
                              width: '50px',
                              height: '50px',
                              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                              borderRadius: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '1.2rem',
                              flexShrink: 0,
                              boxShadow: '0 8px 20px rgba(14, 165, 233, 0.3)',
                              transition: 'all 0.3s ease'
                            }}>
                              <i className="bi bi-check-lg"></i>
                            </div>
                            <div>
                              <h6 style={{ 
                                color: '#1e293b', 
                                fontWeight: '700',
                                fontSize: '1.1rem',
                                margin: 0,
                                lineHeight: '1.4'
                              }}>
                                {feature}
                              </h6>
                              <p style={{
                                color: '#64748b',
                                fontSize: '0.9rem',
                                margin: 0,
                                marginTop: '0.25rem'
                              }}>
                                Production-ready solution
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}

              {/* Industry Use Cases */}
              <div className="mb-6" style={{ marginTop: '4rem' }}>
                <div className="text-center mb-5">
                  <h3 style={{
                    fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)',
                    fontWeight: '800',
                    color: '#1e293b',
                    marginBottom: '1rem',
                    letterSpacing: '-0.02em'
                  }}>
                    <i className={`bi ${serviceId === 'staff-augmentation' ? 'bi-people-fill' : 'bi-building'} me-3`} style={{ color: '#0ea5e9' }}></i>
                    {serviceId === 'staff-augmentation' ? 'Team Integration Success' : 'Industry Applications'}
                  </h3>
                  <p style={{
                    fontSize: '1.1rem',
                    color: '#64748b',
                    maxWidth: '600px',
                    margin: '0 auto',
                    lineHeight: '1.6'
                  }}>
                    {serviceId === 'staff-augmentation' 
                      ? 'Successful team integrations across industries with proven results'
                      : 'Proven solutions across industries with measurable business impact'
                    }
                  </p>
                </div>
                
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                  border: '1px solid rgba(14, 165, 233, 0.1)',
                  borderRadius: '24px',
                  padding: '4rem 2rem',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
                  margin: '0 1rem'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
                      radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.04) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.03) 0%, transparent 50%)
                    `,
                    zIndex: 1
                  }}></div>

                  <div style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(280px, 30vw, 350px), 1fr))',
                      gap: 'clamp(1rem, 3vw, 2rem)',
                      maxWidth: '1200px',
                      margin: '0 auto'
                    }}>
                      {[
                        {
                          industry: 'Healthcare',
                          useCase: 'Medical Documentation AI',
                          result: '60% reduction in documentation time',
                          icon: 'bi-heart-pulse'
                        },
                        {
                          industry: 'Financial Services',
                          useCase: 'Automated Report Generation',
                          result: '80% faster compliance reporting',
                          icon: 'bi-bank'
                        },
                        {
                          industry: 'Retail & CPG',
                          useCase: 'Product Description Generation',
                          result: '90% faster product catalog updates',
                          icon: 'bi-shop'
                        },
                        {
                          industry: 'Transport & Logistics',
                          useCase: 'Route Optimization Systems',
                          result: '30% fuel cost savings',
                          icon: 'bi-truck'
                        },
                        {
                          industry: 'Cybersecurity',
                          useCase: 'Threat Detection Systems',
                          result: '99% threat detection accuracy',
                          icon: 'bi-shield-check'
                        },
                        {
                          industry: 'Education',
                          useCase: 'Personalized Learning Platforms',
                          result: '70% improvement in student engagement',
                          icon: 'bi-mortarboard'
                        }
                      ].map((case_, index) => (
                        <div key={index} style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid rgba(14, 165, 233, 0.1)',
                          borderRadius: 'clamp(12px, 3vw, 20px)',
                          padding: 'clamp(1.5rem, 4vw, 2.5rem) clamp(1rem, 3vw, 2rem)',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          cursor: 'pointer',
                          position: 'relative',
                          overflow: 'hidden',
                          backdropFilter: 'blur(10px)',
                          minHeight: 'clamp(160px, 25vw, 180px)',
                          display: 'flex',
                          alignItems: 'flex-start'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-8px) scale(1.03)';
                          e.currentTarget.style.boxShadow = '0 25px 50px rgba(14, 165, 233, 0.2)';
                          e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0) scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.1)';
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 'clamp(0.75rem, 2vw, 1.5rem)',
                            width: '100%'
                          }}>
                            <div style={{
                              width: 'clamp(50px, 10vw, 70px)',
                              height: 'clamp(50px, 10vw, 70px)',
                              background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
                              borderRadius: 'clamp(12px, 3vw, 18px)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: 'clamp(1.2rem, 3vw, 1.6rem)',
                              flexShrink: 0,
                              boxShadow: '0 10px 25px rgba(14, 165, 233, 0.3)',
                              transition: 'all 0.3s ease'
                            }}>
                              <i className={`bi ${case_.icon}`}></i>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <h6 style={{
                                color: '#1e293b',
                                fontWeight: '700',
                                margin: 0,
                                fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                                lineHeight: '1.3',
                                marginBottom: 'clamp(0.25rem, 1vw, 0.5rem)'
                              }}>
                                {case_.industry}
                              </h6>
                              <p style={{
                                color: '#64748b',
                                fontSize: 'clamp(0.85rem, 2vw, 1rem)',
                                margin: 'clamp(0.25rem, 1vw, 0.5rem) 0',
                                fontWeight: '600',
                                lineHeight: '1.4'
                              }}>
                                {case_.useCase}
                              </p>
                              <p style={{
                                color: '#0ea5e9',
                                fontSize: 'clamp(0.8rem, 1.8vw, 0.95rem)',
                                margin: 0,
                                fontWeight: '700',
                                lineHeight: '1.3'
                              }}>
                                {case_.result}
                              </p>
                            </div>
                          </div>
                      </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Technologies & Tech Stack - Only for non-staff-augmentation services */}
              {serviceId !== 'staff-augmentation' && service.technologies && (
                <div className="mb-6" style={{ marginTop: '4rem' }}>
                  <div className="text-center mb-5">
                    <h3 style={{
                      fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)',
                      fontWeight: '800',
                      color: '#1e293b',
                      marginBottom: '1rem',
                      letterSpacing: '-0.02em'
                    }}>
                      <i className="bi bi-gear-fill me-3" style={{ color: '#0ea5e9' }}></i>
                      Technologies & Tech Stack
                    </h3>
                    <p style={{
                      fontSize: '1.1rem',
                      color: '#64748b',
                      maxWidth: '600px',
                      margin: '0 auto',
                      lineHeight: '1.6'
                    }}>
                      Cutting-edge technologies and proven frameworks
                    </p>
                  </div>
                  
                  <div style={{
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                    border: '1px solid rgba(14, 165, 233, 0.1)',
                    borderRadius: '24px',
                    padding: '3rem',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `
                        radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.04) 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.03) 0%, transparent 50%)
                      `,
                      zIndex: 1
                    }}></div>

                    <div style={{ position: 'relative', zIndex: 2 }}>
                      <div className="row g-4">
                        {service.technologies.map((tech, index) => (
                          <div key={index} className="col-lg-4 col-md-6">
                            <div style={{
                              background: 'rgba(255, 255, 255, 0.8)',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(14, 165, 233, 0.1)',
                              borderRadius: '16px',
                              padding: '1.5rem',
                              textAlign: 'center',
                              transition: 'all 0.3s ease',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-5px)';
                              e.currentTarget.style.boxShadow = '0 10px 25px rgba(14, 165, 233, 0.15)';
                              e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.1)';
                            }}>
                              <div style={{
                                width: '60px',
                                height: '60px',
                                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '1.5rem',
                                margin: '0 auto 1rem',
                                boxShadow: '0 8px 20px rgba(14, 165, 233, 0.3)'
                              }}>
                                <i className={`bi ${tech.icon || 'bi-gear'}`}></i>
                              </div>
                              <h6 style={{
                                color: '#1e293b',
                                fontWeight: '700',
                                fontSize: '1.1rem',
                                margin: '0 0 0.5rem 0'
                              }}>
                                {tech}
                              </h6>
                              <p style={{
                                color: '#64748b',
                                fontSize: '0.9rem',
                                margin: 0,
                                lineHeight: '1.5'
                              }}>
                                Industry-leading technology
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Our Expertise - Only for non-staff-augmentation services */}
              {serviceId !== 'staff-augmentation' && service.expertise && (
                <div className="mb-6" style={{ marginTop: '4rem' }}>
                  <div className="text-center mb-5">
                    <h3 style={{
                      fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)',
                      fontWeight: '800',
                      color: '#1e293b',
                      marginBottom: '1rem',
                      letterSpacing: '-0.02em'
                    }}>
                      <i className="bi bi-award-fill me-3" style={{ color: '#0ea5e9' }}></i>
                      Our Expertise
                    </h3>
                    <p style={{
                      fontSize: '1.1rem',
                      color: '#64748b',
                      maxWidth: '600px',
                      margin: '0 auto',
                      lineHeight: '1.6'
                    }}>
                      Deep domain knowledge and proven methodologies
                    </p>
                  </div>
                  
                  <div style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                    border: '1px solid rgba(14, 165, 233, 0.1)',
                    borderRadius: '24px',
                    padding: '3rem',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `
                        radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.04) 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.03) 0%, transparent 50%)
                      `,
                      zIndex: 1
                    }}></div>

                    <div style={{ position: 'relative', zIndex: 2 }}>
                      <div className="row g-4">
                        {service.expertise.map((expertise, index) => (
                          <div key={index} className="col-lg-6">
                            <div style={{
                              background: 'rgba(255, 255, 255, 0.8)',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(14, 165, 233, 0.1)',
                              borderRadius: '16px',
                              padding: '2rem',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '1rem',
                              transition: 'all 0.3s ease',
                              height: '100%'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-3px)';
                              e.currentTarget.style.boxShadow = '0 10px 25px rgba(14, 165, 233, 0.15)';
                              e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.1)';
                            }}>
                              <div style={{
                                width: '50px',
                                height: '50px',
                                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '1.2rem',
                                flexShrink: 0,
                                boxShadow: '0 8px 20px rgba(14, 165, 233, 0.3)'
                              }}>
                                <i className={`bi ${expertise.icon || 'bi-check-lg'}`}></i>
                              </div>
                              <div>
                                <h6 style={{
                                  color: '#1e293b',
                                  fontWeight: '700',
                                  fontSize: '1.1rem',
                                  margin: '0 0 0.5rem 0'
                                }}>
                                  {expertise}
                                </h6>
                                <p style={{
                                  color: '#64748b',
                                  fontSize: '0.95rem',
                                  margin: 0,
                                  lineHeight: '1.5'
                                }}>
                                  Proven expertise and methodology
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Social Proof & Credibility */}
              <div className="mb-6" style={{ marginTop: '4rem' }}>
                <div className="text-center mb-5">
                  <h3 style={{
                    fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)',
                    fontWeight: '800',
                    color: '#1e293b',
                    marginBottom: '1rem',
                    letterSpacing: '-0.02em'
                  }}>
                    <i className={`bi ${serviceId === 'staff-augmentation' ? 'bi-people-check' : 'bi-shield-check'} me-3`} style={{ color: '#0ea5e9' }}></i>
                    {serviceId === 'staff-augmentation' ? 'Trusted Talent Partners' : 'Trusted by Industry Leaders'}
                  </h3>
                  <p style={{
                    fontSize: '1.1rem',
                    color: '#64748b',
                    maxWidth: '600px',
                    margin: '0 auto',
                    lineHeight: '1.6'
                  }}>
                    {serviceId === 'staff-augmentation' 
                      ? 'Pre-vetted professionals with proven track records and industry expertise'
                      : 'Enterprise-grade security, compliance, and proven results'
                    }
                  </p>
        </div>

              <div style={{
                  background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                  border: '1px solid rgba(14, 165, 233, 0.1)',
                borderRadius: '24px',
                padding: '3rem',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
                      radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.04) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.03) 0%, transparent 50%)
                    `,
                    zIndex: 1
                  }}></div>

                  <div style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(250px, 40vw, 300px), 1fr))',
                      gap: 'clamp(1rem, 3vw, 2rem)'
                    }}>
                      {(serviceId === 'staff-augmentation' ? [
                        {
                          title: 'Pre-Vetted Talent',
                          description: 'Rigorous screening process with technical and cultural fit assessment',
                          icon: 'bi-person-check'
                        },
                        {
                          title: '5+ Years Experience',
                          description: 'Senior-level professionals with proven track records',
                          icon: 'bi-award'
                        },
                        {
                          title: 'Cultural Fit Guarantee',
                          description: 'Ensured team integration and seamless collaboration',
                          icon: 'bi-people-fill'
                        },
                        {
                          title: 'Dedicated Support',
                          description: 'Ongoing management and performance monitoring',
                          icon: 'bi-headset'
                        }
                      ] : [
                        {
                          title: 'Enterprise Security',
                          description: 'SOC 2 Type II, ISO 27001, GDPR compliant',
                          icon: 'bi-shield-lock'
                        },
                        {
                          title: '99.9% Uptime SLA',
                          description: 'Guaranteed availability with 24/7 monitoring',
                          icon: 'bi-activity'
                        },
                        {
                          title: 'Data Privacy',
                          description: 'End-to-end encryption and data sovereignty',
                          icon: 'bi-lock'
                        },
                        {
                          title: 'Expert Support',
                          description: 'Dedicated AI specialists and success managers',
                          icon: 'bi-people'
                        }
                      ]).map((item, index) => (
                        <div key={index} style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid rgba(14, 165, 233, 0.1)',
                          borderRadius: 'clamp(12px, 2.5vw, 16px)',
                          padding: 'clamp(1.5rem, 3vw, 2rem)',
                          textAlign: 'center',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          cursor: 'pointer',
                          position: 'relative',
                          overflow: 'hidden',
                          backdropFilter: 'blur(10px)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
                          e.currentTarget.style.boxShadow = '0 20px 40px rgba(14, 165, 233, 0.15)';
                          e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0) scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.1)';
                        }}>
                          <div style={{
                            width: 'clamp(50px, 10vw, 60px)',
                            height: 'clamp(50px, 10vw, 60px)',
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
                            borderRadius: 'clamp(12px, 2.5vw, 16px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: 'clamp(1.2rem, 3vw, 1.4rem)',
                            margin: '0 auto clamp(1rem, 2.5vw, 1.5rem)',
                            boxShadow: '0 8px 20px rgba(14, 165, 233, 0.3)',
                            transition: 'all 0.3s ease'
                          }}>
                            <i className={`bi ${item.icon}`}></i>
                </div>
                          <h6 style={{
                            color: '#1e293b',
                            fontWeight: '700',
                            margin: '0 0 clamp(0.25rem, 1vw, 0.5rem) 0',
                            fontSize: 'clamp(1rem, 2.2vw, 1.1rem)',
                            lineHeight: '1.4'
                          }}>
                            {item.title}
                          </h6>
                          <p style={{
                            color: '#64748b',
                            fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
                            margin: 0,
                            lineHeight: '1.5'
                          }}>
                            {item.description}
                          </p>
                    </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <CTA {...generateCTAMessage('service', { service: service })} />

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </>
  );
};

export default ServiceDetailPage;
